import React, { useState, useEffect, useRef } from 'react'
import { CampaignChannel, ChannelVariant, ChannelVariantInput } from '../../../types/campaigns'
import { HTMLEditor } from './HTMLEditor'
import { WhatsAppEditor } from './tiptap/WhatsAppEditor'
import { EmailDesignSettings, EmailConfig, DEFAULT_EMAIL_CONFIG } from './EmailDesignSettings'
import { EditorBlockSidebar } from './builder/EditorBlockSidebar'
import { BlockConfigurationModal } from './builder/BlockConfigurationModal'
import { generateBlockHTML } from './builder/blockGenerators'
import { EditorBlock, BlockConfigData, BlockInstance, BLOCKS_LIBRARY } from './builder/types'
import { Accordion, Button, Modal, Form } from 'react-bootstrap'
import { EmailBlockBuilder } from './builder/EmailBlockBuilder'
import { gql, useMutation } from '@apollo/client'

const SEND_CAMPAIGN_EMAIL = gql`
  mutation SendCampaignEmail(
    $toEmail: String!
    $subject: String!
    $htmlContent: String!
    $fromEmail: String
    $fromName: String
    $replyTo: String
    $includeLogo: Boolean
  ) {
    sendCampaignEmail(
      toEmail: $toEmail
      subject: $subject
      htmlContent: $htmlContent
      fromEmail: $fromEmail
      fromName: $fromName
      replyTo: $replyTo
      includeLogo: $includeLogo
    ) {
      success
      errorMessage
      messageId
    }
  }
`

interface VariantEditorProps {
  channel: CampaignChannel
  variant: ChannelVariant | null
  onSave: (variantData: ChannelVariantInput) => void
  onDelete?: () => void
  templateVariables: string[]
  onBodyTextChange?: (bodyText: string) => void
  onMetadataChange?: (metadata: any) => void
}

export const VariantEditor: React.FC<VariantEditorProps> = ({
  channel,
  variant,
  onSave,
  onDelete,
  templateVariables,
  onBodyTextChange,
  onMetadataChange,
}) => {
  const [subject, setSubject] = useState(variant?.subject || '')
  const [bodyText, setBodyText] = useState(variant?.bodyText || '')

  // Notify parent of bodyText changes for real-time preview
  useEffect(() => {
    if (onBodyTextChange) {
      onBodyTextChange(bodyText)
    }
  }, [bodyText, onBodyTextChange])

  const [bodyHtml, setBodyHtml] = useState(variant?.bodyHtml || '')

  // Visual Builder State
  const [headerHtml, setHeaderHtml] = useState('')
  const [footerHtml, setFooterHtml] = useState('')
  const [activeSection, setActiveSection] = useState<'header' | 'body' | 'footer'>('body')

  const [headerBlocks, setHeaderBlocks] = useState<BlockInstance[]>([])
  const [bodyBlocks, setBodyBlocks] = useState<BlockInstance[]>([])
  const [footerBlocks, setFooterBlocks] = useState<BlockInstance[]>([])

  const editorsRef = useRef<{ [key: string]: any }>({})

  const [showBlockModal, setShowBlockModal] = useState(false)
  const [selectedBlock, setSelectedBlock] = useState<EditorBlock | null>(null)

  const [whatsappTemplateId, setWhatsappTemplateId] = useState(variant?.whatsappTemplateId || '')
  const [whatsappLanguage, setWhatsappLanguage] = useState(variant?.whatsappLanguage || 'es')
  const [smsMaxLength, setSmsMaxLength] = useState(variant?.smsMaxLength || 160)
  const [isDefault, setIsDefault] = useState(variant?.isDefault || false)
  const [useHtml, setUseHtml] = useState(false)
  const [showHtmlPreview, setShowHtmlPreview] = useState(false)
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  const [testEmailRecipient, setTestEmailRecipient] = useState('')
  const [testFromEmail, setTestFromEmail] = useState('')
  const [testFromName, setTestFromName] = useState('')
  const [testReplyTo, setTestReplyTo] = useState('')
  const [testIncludeLogo, setTestIncludeLogo] = useState(false)

  // GraphQL Mutation for sending campaign email
  const [sendCampaignEmailMutation, { loading: sendingTestEmail }] = useMutation(SEND_CAMPAIGN_EMAIL)

  // Email Config State (Legacy support + Header/Footer Styles)
  const [emailConfig, setEmailConfig] = useState<EmailConfig>(DEFAULT_EMAIL_CONFIG)
  const [activeTab, setActiveTab] = useState<'content' | 'design'>('content')

  const bodyTextRef = useRef<HTMLTextAreaElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)

  // Reset form when channel or variant changes
  useEffect(() => {
    setSubject(variant?.subject || '')
    setBodyText(variant?.bodyText || '')
    // setBodyHtml(variant?.bodyHtml || '') // Handled below with split logic
    setWhatsappTemplateId(variant?.whatsappTemplateId || '')
    setWhatsappLanguage(variant?.whatsappLanguage || 'es')
    setSmsMaxLength(variant?.smsMaxLength || 160)
    setIsDefault(variant?.isDefault || false)
    setUseHtml(channel === 'EMAIL' ? (variant?.bodyHtml ? true : true) : !!variant?.bodyHtml) // Default true for Email
    setActiveTab('content')

    // Parse email config from metadata
    if (channel === 'EMAIL' && variant?.metadata) {
      try {
        let meta = variant.metadata
        if (typeof meta === 'string') meta = JSON.parse(meta)

        // Load Split Content if available
        if (meta.header_content) setHeaderHtml(meta.header_content)
        else setHeaderHtml('')

        if (meta.footer_content) setFooterHtml(meta.footer_content)
        else setFooterHtml('') // TODO: Auto-insert Unsubscribe if empty?

        // If bodyHtml exists but no split content, maybe we should just load bodyHtml into Body section?
        if (variant?.bodyHtml) {
          setBodyHtml(variant.bodyHtml)
        } else {
          setBodyHtml('')
        }

        if (meta.email_config) {
          setEmailConfig({
            ...DEFAULT_EMAIL_CONFIG,
            ...meta.email_config,
            header: { ...DEFAULT_EMAIL_CONFIG.header, ...meta.email_config.header },
            footer: { ...DEFAULT_EMAIL_CONFIG.footer, ...meta.email_config.footer },
            styles: { ...DEFAULT_EMAIL_CONFIG.styles, ...meta.email_config.styles },
          })
        } else {
          setEmailConfig(DEFAULT_EMAIL_CONFIG)
        }

        // Load Blocks from metadata if available
        if (meta.body_blocks && meta.body_blocks.length > 0) {
          setBodyBlocks(meta.body_blocks)
        } else if (variant?.bodyHtml) {
          // LEGACY IMPORT: Combine header/body/footer into blocks if no blocks exist
          // For now, put the whole bodyHtml into a single 1_col block so they don't lose work
          setBodyBlocks([{
            id: 'import-' + Math.random().toString(36).substr(2, 5),
            blockId: '1_col',
            data: { content: variant.bodyHtml, padding: '0', backgroundColor: 'transparent' }
          }])
        } else {
          setBodyBlocks([])
        }

        if (meta.header_blocks) setHeaderBlocks(meta.header_blocks)
        else if (meta.header_content) setHeaderBlocks([{
          id: 'header-import', blockId: '1_col', data: { content: meta.header_content, padding: '0' }
        }])
        else setHeaderBlocks([])

        if (meta.footer_blocks) setFooterBlocks(meta.footer_blocks)
        else if (meta.footer_content) setFooterBlocks([{
          id: 'footer-import', blockId: '1_col', data: { content: meta.footer_content, padding: '0' }
        }])
        else setFooterBlocks([])

      } catch (e) {
        console.error('Failed to parse email config', e)
        setEmailConfig(DEFAULT_EMAIL_CONFIG)
      }
    } else {
      setEmailConfig(DEFAULT_EMAIL_CONFIG)
      // Reset HTMLs
      if (variant?.bodyHtml) setBodyHtml(variant.bodyHtml)
      else setBodyHtml('')
      setHeaderHtml('')
      setFooterHtml('')
    }
  }, [variant, channel])

  // Notify parent of metadata changes (for immediate preview update)
  useEffect(() => {
    if (channel === 'EMAIL' && onMetadataChange) {
      onMetadataChange({ email_config: emailConfig })
    }
  }, [emailConfig, channel, onMetadataChange])

  const handleSave = () => {
    const metadata = variant?.metadata && typeof variant.metadata === 'object' ? { ...variant.metadata } : {}
    if (channel === 'EMAIL') {
      metadata.email_config = emailConfig
      // Save split content for re-editing
      metadata.header_content = headerHtml
      metadata.footer_content = footerHtml

      // Save blocks for re-editing
      metadata.header_blocks = headerBlocks
      metadata.body_blocks = bodyBlocks
      metadata.footer_blocks = footerBlocks
    }

    // Concatenate for final HTML
    let finalHtml = ''
    if (channel === 'EMAIL' && useHtml) {
      if (headerBlocks.length > 0 || bodyBlocks.length > 0 || footerBlocks.length > 0) {
        // Use generator for blocks
        const finalHeader = headerBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')
        const finalBody = bodyBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')
        const finalFooter = footerBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')

        finalHtml = `
          <!-- HEADER -->
          ${finalHeader}
          <!-- BODY -->
          ${finalBody}
          <!-- FOOTER -->
          ${finalFooter}
        `
      } else {
        // Fallback to direct HTML state if no blocks
        finalHtml = `
              <!-- HEADER -->
              ${headerHtml}
              <!-- BODY -->
              ${bodyHtml}
              <!-- FOOTER -->
              ${footerHtml}
          `
      }
    } else {
      finalHtml = bodyHtml
    }

    const variantData: ChannelVariantInput = {
      channel,
      subject,
      bodyText,
      bodyHtml: channel === 'EMAIL' && useHtml ? finalHtml : undefined,
      whatsappTemplateId: channel === 'WHATSAPP' ? whatsappTemplateId : '',
      whatsappLanguage: channel === 'WHATSAPP' ? whatsappLanguage : 'es',
      smsMaxLength: channel === 'SMS' ? smsMaxLength : 160,
      isDefault,
      metadata: metadata
    }
    onSave(variantData)
  }

  // Generate final email HTML for preview/export
  const getFinalEmailHTML = () => {
    const finalHeaderHtml = headerBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')
    const finalBodyHtml = bodyBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')
    const finalFooterHtml = footerBlocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${subject || 'Email'}</title>
  <style>
    body { margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4; }
    table { border-collapse: collapse; }
    img { border: 0; max-width: 100%; height: auto; }
  </style>
</head>
<body>
  ${finalHeaderHtml}
  ${finalBodyHtml}
  ${finalFooterHtml}
</body>
</html>`
  }

  const copyHTMLToClipboard = () => {
    navigator.clipboard.writeText(getFinalEmailHTML())
    alert('✅ HTML copiado al portapapeles')
  }

  const sendTestEmail = async () => {
    if (!testEmailRecipient || !testEmailRecipient.includes('@')) {
      alert('❌ Por favor ingresa un email válido')
      return
    }

    try {
      const result = await sendCampaignEmailMutation({
        variables: {
          toEmail: testEmailRecipient,
          subject: subject || 'Email de Prueba - TFI',
          htmlContent: getFinalEmailHTML(),
          fromEmail: testFromEmail || undefined,
          fromName: testFromName || undefined,
          replyTo: testReplyTo || undefined,
          includeLogo: testIncludeLogo,
        },
      })

      if (result.data?.sendCampaignEmail?.success) {
        alert(`✅ Email de prueba enviado a ${testEmailRecipient}`)
        setShowTestEmailModal(false)
        setTestEmailRecipient('')
        setTestFromEmail('')
        setTestFromName('')
        setTestReplyTo('')
        setTestIncludeLogo(false)
      } else {
        const errorMsg = result.data?.sendCampaignEmail?.errorMessage || 'Error desconocido'
        alert(`❌ Error: ${errorMsg}`)
      }
    } catch (error: any) {
      console.error('Error sending test email:', error)
      alert(`❌ Error al enviar el email de prueba: ${error.message}`)
    }
  }

  // Builder Handlers
  const handleBlockClick = (block: EditorBlock) => {
    // Zero-Friction Insertion: For structure blocks, insert immediately with defaults
    if (block.type === 'structure' || block.type === 'component' || block.type === 'media') {
      const initialData: BlockConfigData = {
        backgroundColor: block.id === 'hero' ? '#f8f9fa' : '#ffffff',
        padding: block.id === '3_cols' ? '10px' : '20px',
        align: 'center',
        buttonColor: '#007bff',
        buttonText: 'Click Here'
      }

      const newBlock: BlockInstance = {
        id: Math.random().toString(36).substr(2, 9),
        blockId: block.id,
        data: initialData
      }

      if (activeSection === 'header') setHeaderBlocks([...headerBlocks, newBlock])
      else if (activeSection === 'body') setBodyBlocks([...bodyBlocks, newBlock])
      else if (activeSection === 'footer') setFooterBlocks([...footerBlocks, newBlock])

      // Note: We don't show the modal anymore for initial insertion to make it faster.
      // The user can edit settings later via the Gear icon.
    }
  }

  const handleAddBlockById = (blockId: string) => {
    const block = BLOCKS_LIBRARY.find(b => b.id === blockId)
    if (block) handleBlockClick(block)
  }

  const handleBlockConfirm = (data: BlockConfigData) => {
    if (selectedBlock) {
      const newBlock: BlockInstance = {
        id: Math.random().toString(36).substr(2, 9),
        blockId: selectedBlock.id,
        data: data
      }

      if (activeSection === 'header') setHeaderBlocks([...headerBlocks, newBlock])
      else if (activeSection === 'body') setBodyBlocks([...bodyBlocks, newBlock])
      else if (activeSection === 'footer') setFooterBlocks([...footerBlocks, newBlock])
    }
    setShowBlockModal(false)
    setSelectedBlock(null)
  }

  // Effect to sync blocks to HTML
  useEffect(() => {
    const generateHtml = (blocks: BlockInstance[]) => {
      return blocks.map(b => generateBlockHTML(b.blockId, b.data)).join('\n')
    }
    setHeaderHtml(generateHtml(headerBlocks))
    setBodyHtml(generateHtml(bodyBlocks))
    setFooterHtml(generateHtml(footerBlocks))
  }, [headerBlocks, bodyBlocks, footerBlocks])

  const renderChannelSpecificFields = () => {
    switch (channel) {
      case 'EMAIL':
        return (
          <>
            <div className='mb-5'>
              <label className='form-label required'>Subject</label>
              <input
                ref={subjectRef}
                type='text'
                className='form-control'
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder='Example: Welcome to our service'
              />
            </div>

            <div className='variant-template-contenido card mb-2 border'>
              <div className='card-header p-0 border-bottom d-flex justify-content-between align-items-center'>
                <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 px-4 pt-2'>
                  <li className='nav-item'>
                    <a
                      className={`nav-link ${activeTab === 'content' ? 'active' : ''} `}
                      onClick={() => setActiveTab('content')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className='bi bi-pencil-square me-2'></i>Content
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a
                      className={`nav-link ${activeTab === 'design' ? 'active' : ''} `}
                      onClick={() => setActiveTab('design')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className='bi bi-palette me-2'></i>Styles
                    </a>
                  </li>
                </ul>

                {activeTab === 'content' && (
                  <>
                    <div className='form-check form-switch me-4 my-2'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='useHtmlSwitch'
                        checked={useHtml}
                        onChange={(e) => setUseHtml(e.target.checked)}
                      />
                      <label className='form-check-label small fw-bold' htmlFor='useHtmlSwitch'>
                        Visual Editor
                      </label>
                    </div>
                    {useHtml && (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="my-2 me-2"
                          onClick={() => setShowHtmlPreview(true)}
                        >
                          <i className="bi bi-code-square me-1"></i> View HTML
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="my-2"
                          onClick={() => setShowTestEmailModal(true)}
                        >
                          <i className="bi bi-envelope-check me-1"></i> Send Test
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className='card-body p-0'>
                {activeTab === 'content' ? (
                  <>
                    {/* Switch moved to header */}

                    {useHtml ? (
                      <div className="d-flex w-100" style={{ height: '800px', overflow: 'hidden' }}>
                        {/* LEFT SIDEBAR */}
                        < EditorBlockSidebar onBlockClick={handleBlockClick} />

                        {/* RIGHT CANVAS (ACCORDION) */}
                        <div className="flex-grow-1 p-4 bg-gray-100" style={{ overflowY: 'auto' }}>
                          <div className="email-builder-canvas mx-auto" style={{ maxWidth: '100%', paddingLeft: '40px', paddingRight: '40px' }}>
                            <Accordion activeKey={activeSection} className="builder-accordion">
                              <Accordion.Item eventKey="header" className="border-0 mb-3 shadow-sm rounded overflow-hidden">
                                <Accordion.Header onClick={() => setActiveSection('header')}>
                                  <span className="fw-bold text-gray-700"><i className="bi bi-layout-text-window-reverse me-2"></i> Header</span>
                                </Accordion.Header>
                                <Accordion.Body className="p-0 bg-white">
                                  <EmailBlockBuilder
                                    blocks={headerBlocks}
                                    onChange={setHeaderBlocks}
                                    onAddBlock={handleAddBlockById}
                                    templateVariables={templateVariables}
                                  />
                                </Accordion.Body>
                              </Accordion.Item>

                              <Accordion.Item eventKey="body" className="border-0 mb-3 shadow-sm rounded overflow-hidden">
                                <Accordion.Header onClick={() => setActiveSection('body')}>
                                  <span className="fw-bold text-gray-700"><i className="bi bi-body-text me-2"></i> Body</span>
                                </Accordion.Header>
                                <Accordion.Body className="p-0 bg-white shadow-lg">
                                  <EmailBlockBuilder
                                    blocks={bodyBlocks}
                                    onChange={setBodyBlocks}
                                    onAddBlock={handleAddBlockById}
                                    templateVariables={templateVariables}
                                  />
                                </Accordion.Body>
                              </Accordion.Item>

                              <Accordion.Item eventKey="footer" className="border-0 shadow-sm rounded overflow-hidden">
                                <Accordion.Header onClick={() => setActiveSection('footer')}>
                                  <span className="fw-bold text-gray-700"><i className="bi bi-layout-text-sidebar-reverse me-2"></i> Footer</span>
                                </Accordion.Header>
                                <Accordion.Body className="p-0 bg-white">
                                  <EmailBlockBuilder
                                    blocks={footerBlocks}
                                    onChange={setFooterBlocks}
                                    onAddBlock={handleAddBlockById}
                                    templateVariables={templateVariables}
                                  />
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='alert alert-warning m-4'>
                        Activate "Use Visual Editor" to design the body of the email.
                        <br />
                        If you prefer plain text, use the field below.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-4">
                    <EmailDesignSettings
                      config={emailConfig}
                      onChange={setEmailConfig}
                    />
                  </div>
                )}
              </div>
            </div >

            {/* Block Config Modal */}
            < BlockConfigurationModal
              show={showBlockModal}
              onHide={() => setShowBlockModal(false)}
              block={selectedBlock}
              onConfirm={handleBlockConfirm}
            />
          </>
        )

      case 'WHATSAPP':
        // ... (Existing WhatsApp code)
        return (
          <>
            <div className='alert alert-info mb-5'>
              <strong>💡 Meta WhatsApp Templates</strong>
              <p className='mb-2'>
                For marketing campaigns, you must use templates approved by Meta.
              </p>
              <ul className='mb-0'>
                <li>Create templates in Meta Business Manager</li>
                <li>Wait for approval (24-48h)</li>
                <li>Enter the ID here</li>
              </ul>
            </div>

            <div className='mb-5'>
              <label className='form-label'>Meta WhatsApp Template ID (optional)</label>
              <input
                type='text'
                className='form-control'
                value={whatsappTemplateId}
                onChange={(e) => setWhatsappTemplateId(e.target.value)}
                placeholder='Ej: payment_reminder'
              />
              <div className='form-text'>
                If you don't use template, it will only work in active sessions (24h)
              </div>
            </div>

            <div className='mb-5'>
              <label className='form-label'>Language</label>
              <select
                className='form-select'
                value={whatsappLanguage}
                onChange={(e) => setWhatsappLanguage(e.target.value)}
              >
                <option value='es'>Spanish</option>
                <option value='en'>English</option>
                <option value='pt'>Portuguese</option>
              </select>
            </div>
          </>
        )
      case 'SMS':
        // ... (Existing SMS code)
        return (
          <></>
        )
      default:
        return null
    }
  }

  return (
    <>
      <div className='card'>
        <div className='card-header variant-editor-header'>
          <h3 className='card-title'>
            {variant ? 'Edit Variant' : 'New Variant'} - {channel}
          </h3>
        </div>
        <div className='card-body'>
          {/* Channel-specific fields */}
          {renderChannelSpecificFields()}

          {/* Common field: Body Text - Show only if NOT Email HTML mode */}
          {!(channel === 'EMAIL' && useHtml) && (
            <div className='mb-5'>
              <label className='form-label required'>
                Message Body
                {channel === 'EMAIL' && ' (Text Plain / Fallback)'}
              </label>

              {/* Use WhatsApp Editor for WHATSAPP channel */}
              {channel === 'WHATSAPP' ? (
                <>
                  <WhatsAppEditor
                    value={bodyText}
                    onChange={setBodyText}
                    height={300}
                    placeholder='Ej: Hello {{client_first_name}}, your payment is due on {{policy_effective_date}}'
                    variables={templateVariables}
                  />
                  <div className='form-text'>
                    <strong>💡 Tip:</strong> Use the <strong>Variables</strong> button in the toolbar to insert variables easily.
                    You can also write manually using <code>{'{{variable_name}}'}</code>
                  </div>
                </>
              ) : (
                <>
                  <textarea
                    ref={bodyTextRef}
                    className='form-control'
                    rows={channel === 'SMS' ? 4 : 8}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    placeholder={`Ej: Hello {{client_first_name}}, your payment is due on {{policy_effective_date}}`}
                  />
                  <div className='footer-message-body'>
                    <div className='form-text'>
                      <strong>Tip:</strong> Click on variables above to insert them, or type manually using{' '}
                      <code>{'{{variable_name}}'}</code> format
                    </div>
                    {channel === 'SMS' && (
                      <div className='mb-5'>
                        <div className='form-text'>
                          Characters: {bodyText.length} / {smsMaxLength}
                          {bodyText.length > smsMaxLength && (
                            <span className='text-danger ms-2'>⚠️ Excede el límite</span>
                          )}
                        </div>
                      </div>
                    )}</div>
                </>
              )}
            </div>
          )}


          {/* Default variant checkbox */}
          <div className='mb-5'>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                id='isDefaultCheck'
              />
              <label className='form-check-label' htmlFor='isDefaultCheck'>
                Mark as default variant
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className='d-flex justify-content-between'>
            <button onClick={handleSave} className='btn btn-primary'>
              {variant ? '💾 Update' : '➕ Create'} Variant
            </button>

            {variant && onDelete && (
              <button onClick={onDelete} className='btn btn-danger'>
                🗑️ Delete Variant
              </button>
            )}
          </div>
        </div>
      </div>

      {/* HTML Preview Modal */}
      <Modal show={showHtmlPreview} onHide={() => setShowHtmlPreview(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-code-square me-2"></i> Final HTML Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted small mb-2">
              This is the complete HTML code that will be used to send the email. You can copy it and use it in any email sending platform.
            </p>
            <div className="d-flex gap-2 mb-3">
              <Button variant="primary" size="sm" onClick={copyHTMLToClipboard}>
                <i className="bi bi-clipboard me-1"></i> Copy HTML
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => {
                const blob = new Blob([getFinalEmailHTML()], { type: 'text/html' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `email-${subject || 'template'}.html`
                a.click()
                URL.revokeObjectURL(url)
              }}>
                <i className="bi bi-download me-1"></i> Download HTML
              </Button>
            </div>
          </div>
          <pre className="bg-light p-3 rounded" style={{ maxHeight: '500px', overflow: 'auto', fontSize: '12px' }}>
            <code>{getFinalEmailHTML()}</code>
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHtmlPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Test Email Modal */}
      <Modal show={showTestEmailModal} onHide={() => setShowTestEmailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-envelope-check me-2"></i> Send Test Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info small mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Tip:</strong> Send the email to your own address to verify how it looks in different email clients (Gmail, Outlook, etc.)
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Email of the Recipient</Form.Label>
            <Form.Control
              type="email"
              placeholder="tu@email.com"
              value={testEmailRecipient}
              onChange={(e) => setTestEmailRecipient(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  sendTestEmail()
                }
              }}
              autoFocus
            />
            <Form.Text className="text-muted">
              You can use multiple emails separated by commas
            </Form.Text>
          </Form.Group>

          <hr className="my-3" />
          <h6 className="mb-3">⚙️ Advanced Options (Optional)</h6>

          <Form.Group className="mb-3">
            <Form.Label>From Email (Remitente)</Form.Label>
            <Form.Control
              type="email"
              placeholder="mailing@yourcompany.com (optional)"
              value={testFromEmail}
              onChange={(e) => setTestFromEmail(e.target.value)}
            />
            <Form.Text className="text-muted">
              Leave empty to use the default email
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>From Name (Name of the sender)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Marketing Team (optional)"
              value={testFromName}
              onChange={(e) => setTestFromName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Reply-To (Reply To Email)</Form.Label>
            <Form.Control
              type="email"
              placeholder="support@yourcompany.com (optional)"
              value={testReplyTo}
              onChange={(e) => setTestReplyTo(e.target.value)}
            />
            <Form.Text className="text-muted">
              Where the user responses will go
            </Form.Text>
          </Form.Group>

          <Form.Check
            type="checkbox"
            id="include-logo-check"
            label="Embed logo in the email (optional)"
            checked={testIncludeLogo}
            onChange={(e) => setTestIncludeLogo(e.target.checked)}
            className="mb-3"
          />

          <div className="small text-muted">
            <strong>Subject:</strong> {subject || 'Test Email'}<br />
            <strong>Blocks:</strong> {headerBlocks.length + bodyBlocks.length + footerBlocks.length} blocks in total
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestEmailModal(false)} disabled={sendingTestEmail}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={sendTestEmail}
            disabled={sendingTestEmail || !testEmailRecipient}
          >
            {sendingTestEmail ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending...
              </>
            ) : (
              <>
                <i className="bi bi-send me-1"></i> Send Test
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
